import { ResourceDataType, ResourceErrorsType } from '@components/SummarySelection/data/createResource';
import { emptySummary, SummaryStateType } from '@components/SummarySelection/data/summaryState';
import { mapIdSlugCollection } from '@lib/mapIdSlugCollection';
import { PackageType } from '@model/Package';
import { SkuType } from '@model/Sku';
import { Action, createActions } from '@packages/context/lib/action';
import { createContext, ProviderProps } from '@packages/context/lib/createContext';
import { call } from '@packages/functions';
import { Record } from 'immutable';
import { cloneDeep, find, orderBy } from 'lodash';
import { MutableRefObject } from 'react';

export type SummaryProviderProps = ProviderProps<SummaryStateType>;

export type SummaryActionsType =
    | 'setData'
    | 'setErrors'
    | 'setLoading'
    | 'selectPackage'
    | 'editPackage'
    | 'toggleSku'
    | 'setCategory';

const initialState = Record<SummaryStateType>(emptySummary());
const actions = createActions<SummaryActionsType>([
    'setData',
    'setErrors',
    'setLoading',
    'selectPackage',
    'editPackage',
    'toggleSku',
    'setCategory',
]);

const { Context, Provider } = createContext<SummaryStateType, typeof actions>({
    initialState,
    actions,
    name: 'Summary',

    debug: false,

    events: {
        onSetData(
            state: Record<SummaryStateType>,
            action: Action<MutableRefObject<ResourceDataType>>,
        ): Record<SummaryStateType> {
            state = state.set('rawData', { ...action.payload.current }).set('data', { ...action.payload.current });

            if (!state.get('isDR') && state.get('package_skus')) {
                const packageSkus = state.get('package_skus') || [];

                Object.entries(
                    packageSkus.reduce(
                        (acc, item) => ({ ...acc, [item.package_id]: [...(acc[item.package_id] || []), item] }),
                        {},
                    ),
                ).forEach(([package_id, item]: any) => {
                    state = this.onEditPackage(state, {
                        payload: { item: { id: parseInt(package_id) }, skus: item },
                    } as any);
                });
            }

            const skus = state.getIn(['data', 'skus'])?.items;
            return state.set('skus_ids', mapIdSlugCollection(state.get('skus_ids'), skus));
        },
        onSetError(
            state: Record<SummaryStateType>,
            action: Action<MutableRefObject<ResourceErrorsType>>,
        ): Record<SummaryStateType> {
            return state.set('errors', action.payload.current);
        },
        onSetLoading(state: Record<SummaryStateType>, action: Action<boolean>): Record<SummaryStateType> {
            return state.set('loading', action.payload);
        },
        onSelectPackage(
            state: Record<SummaryStateType>,
            action: Action<PackageType>,
            { actions }: any,
        ): Record<SummaryStateType> {
            state = state.set('data', state.get('rawData'));
            if (state.get('isDR')) {
                if ((state.get('products') || []).find((pkg) => pkg.id === action.payload.id)) {
                    return state.set(
                        'products',
                        state.get('products').filter((pkg) => pkg.id !== action.payload.id),
                    );
                }

                const packages = state.getIn(['rawData', 'packages', 'items']) || [action.payload];
                const pkg: PackageType = find(packages, ['id', action.payload.id]);

                const pkgSubcategories = [];
                pkg.subcategories.forEach((subcategory) => {
                    const { subcategory_to_repeat } = subcategory;
                    subcategory.subcategory_to_repeat = 1;
                    for (let i = 1; i <= subcategory_to_repeat; i++) {
                        pkgSubcategories.push(subcategory);
                    }
                });
                pkg.subcategories = pkgSubcategories;

                call(state.get('onAdd'), 'package', [...(state.get('products') || []), pkg], state, actions);

                return state
                    .set('products', [...(state.get('products') || []), pkg])
                    .set('package', pkg)
                    .set('package_id', pkg.id);
            }

            if (state.get('package_id') === action.payload.id) {
                return state.set('package', null).set('package_id', null);
            }

            const packages = state.getIn(['rawData', 'packages', 'items']) || [action.payload];
            const pkg = find(packages, ['id', action.payload.id]);

            call(state.get('onAdd'), 'package', pkg, state, actions);

            return state.set('package', pkg).set('package_id', pkg.id);
        },
        onToggleSku(
            state: Record<SummaryStateType>,
            action: Action<SkuType>,
            { actions }: any,
        ): Record<SummaryStateType> {
            const skusCollection: any = state.getIn(['rawData', 'skus', 'items']) || [action.payload];
            let skus = state.get('skus_ids') || [];
            let skuItems: any = state.get('skus') ?? [];
            const categoryId = state.get('category_id');
            if (state.get('radio')) {
                skus = [];
            }
            const isDR = state.get('isDR');
            if (
                (isDR && skuItems.find(({ id }) => id === action.payload.id)) ||
                (!isDR && skus.includes(action.payload.id))
            ) {
                skus = skus.filter((id) => id !== action.payload.id);
                skuItems = skuItems.filter(({ id }) => id !== action.payload.id);
            } else {
                skus = [...skus, action.payload.id];
                const found = skusCollection.find(({ id }) => action.payload.id === id);
                const subcategory =
                    found.categories_subcategories.find(({ category_id }) => category_id === categoryId) ??
                    found.categories_subcategories[0];

                const sku = {
                    id: found.id,
                    name: found.name,
                    subcategory_id: subcategory.id,
                    category_id: subcategory.category_id,
                    minimum_order_quantity: found.minimum_order_quantity,
                    international_minimum_order_quantity: found.international_minimum_order_quantity,
                };
                skuItems.unshift(sku);
            }

            call(state.get('onAdd'), 'sku', skuItems, state, actions);

            return state.set('skus', skuItems).set('skus_ids', skus);
        },
        onEditPackage(
            state: Record<SummaryStateType>,
            action: Action<{ item: PackageType; skus: { subcategory_id: number; sku_id: number }[] }>,
        ): Record<SummaryStateType> {
            const { item, skus } = action.payload;
            const skusList = cloneDeep(state.getIn(['rawData', 'skus', 'items']) || []);
            const packagesList = cloneDeep(state.getIn(['rawData', 'packages', 'items']) || []);

            state = state.set(
                'package_skus',
                skus.map(({ subcategory_id, sku_id }) => ({ sku_id, subcategory_id, package_id: item.id })),
            );

            state = state.setIn(
                ['data', 'packages', 'items'],
                [...packagesList].map((pkg) => {
                    if (item.id === pkg.id && pkg.subcategories) {
                        const subcategories = orderBy(pkg.subcategories, ({ id }) => id);
                        orderBy(skus, ({ subcategory_id }) => subcategory_id).forEach(({ sku_id }, idx) => {
                            if (subcategories[idx]) {
                                subcategories[idx] = {
                                    ...subcategories[idx],
                                    default_sku: find(skusList, ['id', sku_id]),
                                };
                                subcategories[idx]._custom = true;
                            }
                        });

                        return { ...pkg, subcategories };
                    }

                    return pkg;
                }),
            );
            if (state.get('isDR')) {
                const pkgs = state.get('products').filter((pkg) => pkg.id !== item.id);
                pkgs.push(find(state.getIn(['data', 'packages', 'items']), ['id', item.id]));
                return state
                    .set('products', pkgs)
                    .set('package', find(state.getIn(['data', 'packages', 'items']), ['id', item.id]))
                    .set('package_id', item.id);
            }

            return state
                .set('package', find(state.getIn(['data', 'packages', 'items']), ['id', item.id]))
                .set('package_id', item.id);
        },
        onSetCategory(state: Record<SummaryStateType>, action: Action<{ category_id }>): Record<SummaryStateType> {
            return state.set('category_id', action.payload.category_id);
        },
    },
});

export const SummaryContext = Context;
export const SummaryProvider = Provider;
